import React from "react";
import { useRootStore } from "store";
import { useGlobalContext } from "app/store/GlobalContext";
import MenuDropdown from "components/menu/MenuDropdown/MenuDropdown";
import {
  Settings24Regular,
  ChatHelp24Regular,
  PersonSupport24Regular,
  SignOut24Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
} from "@fluentui/react-icons";

import "./UserProfile.scss";

type Props = {
  onLogout: () => void;
};

export default function UserProfile({ onLogout }: Props) {
  const { handleNavigation } = useGlobalContext();
  const { user } = useRootStore((state) => state);
  const { staticInformation } = useRootStore((state) => state.global);

  const isInternalUser = user.userInfo && user.userInfo.type === "Internal";

  const givenName = user.userInfo?.givenName || "";
  const surName = user.userInfo?.surName || "";
  const email = user.userInfo?.email || "";

  // Generate initials from first and last name
  const initials = `${givenName.charAt(0)}${surName.charAt(0)}`.toUpperCase();

  const settingsMenuItem = {
    content: (
      <div className="userProfileMenuItem">
        <Settings24Regular className="userProfileMenuIcon" />
        <span>Mina inställningar</span>
      </div>
    ),
    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      handleNavigation({
        url: "/settings",
        eventTitle: "När du klickar på Mina inställningar kan du göra inställningar för Notifieringar till din e-post när du har fått ett nytt meddelande.",
        e: e
      });
    },
  };

  // Help links
  const helpMenuItems: Array<{
    content: React.ReactElement;
    onClick: () => void;
  }> = [];

  // Help link for all users
  if (staticInformation?.supportContactInformation?.externalSupportSite) {
    helpMenuItems.push({
      content: (
        <div className="userProfileMenuItem">
          <ChatHelp24Regular className="userProfileMenuIcon" />
          <span>Hjälp</span>
        </div>
      ),
      onClick: () => {
        window.open(
          staticInformation.supportContactInformation.externalSupportSite,
          "_blank"
        );
      },
    });
  }

  // Customer support for internal users
  if (isInternalUser && staticInformation?.supportContactInformation?.internalSupportSite) {
    helpMenuItems.push({
      content: (
        <div className="userProfileMenuItem">
          <PersonSupport24Regular className="userProfileMenuIcon" />
          <span>Support</span>
        </div>
      ),
      onClick: () => {
        window.open(
          staticInformation.supportContactInformation.internalSupportSite,
          "_blank"
        );
      },
    });
  }

  const logoutMenuItem = {
    content: (
      <div className="userProfileMenuItem">
        <SignOut24Regular className="userProfileMenuIcon" />
        <span>Logga ut</span>
      </div>
    ),
    onClick: () => onLogout(),
  };

  const allMenuItems = [
    settingsMenuItem,
    ...(helpMenuItems.length > 0 ? [{ hasDivider: true }, ...helpMenuItems] : []),
    { hasDivider: true },
    logoutMenuItem
  ];

  return (
    <div className="userProfile">
      <div className="userProfileToggle">
        <div className="userProfileAvatar">
          <span>{initials}</span>
        </div>
        <div className="userProfileInfo">
          <div className="userProfileName">
            {givenName} {surName}
          </div>
          <div className="userProfileEmail">{email}</div>
        </div>
        <MenuDropdown
          toggleText=""
          menuItems={allMenuItems}
          renderToggleContent={(isOpen) =>
            isOpen ? (
              <ChevronUp20Regular className="chevronDownIcon" />
            ) : (
              <ChevronDown20Regular className="chevronDownIcon" />
            )
          }
        />
      </div>
    </div>
  );
}
